/**
 * Typed based on data returned from API (17/03/2026)
 * Commented out types we don't explicitly rely on to avoid type errors in case data changes (can be uncommented to use them)
 * Some properties seem like enums, but using string for safety (enum values in comments)
 */
export interface JetsLocation {
  id: number;
  name: string;
  store_local_reference: string;
  // created_at: string;
  // updated_at: string;
  // status: string; // "Open", "Perm Closed", "Temp Closed", "Coming Soon"
  // opendate: string | null;
  // closedate: string | null;
  // address_1: string | null;
  // address_2: string;
  // city: string | null;
  // state: string | null;
  // timezone: string | null; // "America/Chicago", "America/Denver", "America/Los_Angeles", "America/New_York", "America/Phoenix"
  // zip_code: string | null;
  // dma: string | null;
  // dma_id: number | null;
  // is_active: boolean;
  // ordering_platform: string | null; // "Loke", "HungerRush", "SpeedDine", "Thrive", "Order Talk", "Rto", "Other"
  // latitude: number | null;
  // longitude: number | null;
  // store_phone: string;
  // ordering_url: string;
  // text_to_order_url: string;
  // text_to_order_note: string; // "", "Test", "Text your order to get 15% off...", "Text your order to get 20% off..."
  // pro_order_url: string;
  // pro_order_note: string; // "", "Temporarily Closed", "Order smart with AI..."
  // keep_gmb_update: boolean;
  // distance: null; // null unless location is provided in request
  // companymanaged: boolean;
  // store_email: null; // always null so far
  // customer_complaint_emails: null; // always null so far
  // store_employment_link: string;
  // managed_by_cec: boolean | null;
  // kiosk: boolean | null;
  // text_campaign_form_url: string | null;
  // speedlinestoreid: string | null;
  // hungerrushid: string | null;
}

export interface JetsLocationWithRef extends JetsLocation {
  ref: string;
}

export class JetsApiTimeoutError extends Error {
  constructor() {
    super("Jets API request timed out");
  }
}

export class JetsApiError extends Error {}

function jetsLocationNameToLocationRef(name: string): string {
  return name.toLowerCase().replace("-", "");
}

function addRefToJetsLocations(
  locations: JetsLocation[],
): JetsLocationWithRef[] {
  return locations.map((location) => ({
    ...location,
    ref: jetsLocationNameToLocationRef(location.name),
  }));
}

/**
 * Fetches all Jets Pizza locations from the Jets API.
 * - server components: call this function directly (avoids unnecessary HTTP round-trip)
 * - client components: fetch from /api/jets/locations (which calls this internally)
 * See: https://nextjs.org/docs/app/building-your-application/data-fetching/fetching
 */
export async function fetchJetsLocations(): Promise<JetsLocationWithRef[]> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);

  let res: Response;
  try {
    // You can get the data for a single store with "https://bid.api.jetspizza.com/general_stores/[storeId]"
    // The storeId is an internal Jet's primary key (see JetsLocation.id), which we don't store, so to get data for 1 location, we need to get all locations and find by name/ref field.
    res = await fetch("https://bid.api.jetspizza.com/general_stores", {
      next: { revalidate: 86400 }, // cached by Next.js Data Cache for 24 hours (no update needed when upgrading from Next 14 to 15)
      signal: controller.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new JetsApiTimeoutError();
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    throw new JetsApiError(`Jets API responded with status ${res.status}`);
  }

  const data = await res.json();

  if (!Array.isArray(data?.data)) {
    throw new JetsApiError("Jets API payload missing `data` array");
  }

  return addRefToJetsLocations(data.data as JetsLocation[]);
}

export async function fetchJetsLocationByRef(
  locationRef?: string,
): Promise<JetsLocationWithRef | null> {
  if (!locationRef) return null;
  const jetsLocations = await fetchJetsLocations();
  return jetsLocations.find((l) => l.ref === locationRef) ?? null;
}
